import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { arClass } from "../../../../utils/useArClass";
import Txt from "../../../Text";
import CustomSelect from "../../../CustomSelect";
import api from "../../../../api";
import { showAlert } from "../../../../redux/alert/alertActions";
import { ArrowLeft, Save, Loader, HelpCircle, CheckCircle, AlertCircle, Zap } from "lucide-react";
import textTranslator from "../../../../utils/useTextTranslator";

const Text = ({ children, className, page = "MY_REQUESTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const priorityOptions = {
  LOW: {
    label: {
      en: "Low",
      ar: "منخفض",
      fr: "Faible",
    },
    value: "LOW",
  },
  MEDIUM: {
    label: {
      en: "Medium",
      ar: "متوسط",
      fr: "Moyen",
    },
    value: "MEDIUM",
  },
  HIGH: {
    label: {
      en: "High",
      ar: "مرتفع",
      fr: "Élevé",
    },
    value: "HIGH",
  },
  CRITICAL: {
    label: {
      en: "Critical",
      ar: "حرج",
      fr: "Critique",
    },
    value: "CRITICAL",
  },
};

const categoryOptions = {
  TECHNICAL_SUPPORT: {
    label: {
      en: "Technical Support",
      ar: "الدعم الفني",
      fr: "Support Technique",
    },
    value: "TECHNICAL_SUPPORT",
  },
  ACCOUNT_ISSUE: {
    label: {
      en: "Account Issue",
      ar: "مشكلة في الحساب",
      fr: "Problème de Compte",
    },
    value: "ACCOUNT_ISSUE",
  },
  FEATURE_REQUEST: {
    label: {
      en: "Feature Request",
      ar: "طلب ميزة",
      fr: "Demande de Fonctionnalité",
    },
    value: "FEATURE_REQUEST",
  },
  BUG_REPORT: {
    label: {
      en: "Bug Report",
      ar: "تقرير خلل",
      fr: "Rapport de Bogue",
    },
    value: "BUG_REPORT",
  },
  GENERAL_INQUIRY: {
    label: {
      en: "General Inquiry",
      ar: "استفسار عام",
      fr: "Demande Générale",
    },
    value: "GENERAL_INQUIRY",
  },
};

const CreateHelpRequest = ({ onCancel }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "GENERAL_INQUIRY",
    priority: "MEDIUM",
    attachments: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  };

  const removeAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim()) {
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: textTranslator("Subject is required", "MY_REQUESTS"),
        })
      );
      return;
    }

    if (!formData.description.trim()) {
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: textTranslator("Description is required", "MY_REQUESTS"),
        })
      );
      return;
    }

    setLoading(true);

    try {
     
      await api.post("it-requests", {
        title: formData.subject,
        description: formData.description,
        requestType: "HELP_SUPPORT",
        category: formData.category,
        priority: formData.priority,

      });

      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "MY_REQUESTS"),
          message: textTranslator("Help request created successfully", "MY_REQUESTS"),
        })
      );

      navigate("/my-requests");
    } catch (error) {
      console.error("Error creating help request:", error);
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: error?.response?.data?.message || "Failed to create help request",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={arClass(`min-h-screen ${
        darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" : "bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
      } transition-colors duration-300 py-8`)}
    >
      <div className={arClass(" mx-auto px-4 sm:px-6 lg:px-8")}>
        {/* Back Button */}
        <motion.button
          onClick={onCancel}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={arClass(
            `flex items-center gap-2 px-4 py-2 rounded-lg font-medium mb-8 transition-all ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700"
                : "bg-white/50 hover:bg-white text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm"
            }`
          )}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <Text>Back</Text>
        </motion.button>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={arClass(
            `rounded-2xl overflow-hidden shadow-2xl border ${
              darkMode
                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700/50"
                : "bg-white border-gray-200/50"
            }`
          )}
        >
          {/* Header Section */}
          <div className={arClass(`bg-gradient-to-r from-amber-600 to-orange-600 px-2 py-4 sm:p-6 relative overflow-hidden`)}>
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                background: [
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <div className={arClass("relative z-10")}>
              <div className={arClass("flex items-center gap-3 mb-2")}>
                <HelpCircle className="text-white w-10 h-10 max-sm:w-6 max-sm:h-6" />
                <h1 className="text-3xl max-sm:text-xl font-bold text-white">
                  <Text>Create Help Request</Text>
                </h1>
              </div>
              <p className={arClass("text-amber-100 text-sm")}>
                <Text>Submit a help request and our team will assist you</Text>
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={arClass(`px-2 py-4 sm:p-6`)}>
            <form onSubmit={handleSubmit} className={arClass("normal-form space-y-6")}>
              {/* Subject Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={arClass(
                  `p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-4")}>
                  <CheckCircle className={arClass("text-lg " + (darkMode ? "text-amber-400" : "text-amber-600"))} />
                  <h2 className={arClass(`text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                    <Text>Subject</Text>
                  </h2>
                </div>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={textTranslator("Enter subject of your help request", "MY_REQUESTS")}
                  className={arClass(
                    `w-full px-4 py-3 rounded-lg border transition-all ${
                      darkMode
                        ? "bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-500 focus:border-amber-500"
                        : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-amber-500"
                    } focus:outline-none`
                  )}
                  required
                />
              </motion.div>

              {/* Category & Priority Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={arClass(
                  `p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-4")}>
                  <Zap className={arClass("text-lg " + (darkMode ? "text-amber-400" : "text-amber-600"))} />
                  <h2 className={arClass(`text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                    <Text>Category & Priority</Text>
                  </h2>
                </div>
                <div className={arClass("grid grid-cols-1 md:grid-cols-2 gap-4")}>
                  <div>
                    <label className={arClass(`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Category</Text>
                    </label>
                    <CustomSelect
                      options={
                        Object.entries(categoryOptions).map(([key, value]) => ({ value: key, label: value.label[defaultLanguage] }))
                      }
                      value={formData.category}
                      onChange={(e) => handleSelectChange("category", e.target.value)}
                      allowCreate={false}
                              allowSearch={false}
                              className="max-sm:text-sm mt-4"
                    />
                  </div>
                  <div>
                    <label className={arClass(`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Priority</Text>
                    </label>
                    <CustomSelect
                      options={Object.entries(priorityOptions).map(([key, value]) => ({ value: key, label: value.label[defaultLanguage] }))}
                      value={formData.priority}
                      onChange={(e) => handleSelectChange("priority", e.target.value)}
                      allowCreate={false}
                              allowSearch={false}
                              className="max-sm:text-sm mt-4"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Description Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={arClass(
                  `p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-4")}>
                  <AlertCircle className={arClass("text-lg " + (darkMode ? "text-amber-400" : "text-amber-600"))} />
                  <h2 className={arClass(`text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                    <Text>Description</Text>
                  </h2>
                </div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={textTranslator("Provide a detailed description of your issue or request...", "MY_REQUESTS")}
                  rows="6"
                  className={arClass(
                    `w-full px-4 py-3 rounded-lg border transition-all resize-none ${
                      darkMode
                        ? "bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-500 focus:border-amber-500"
                        : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-amber-500"
                    } focus:outline-none`
                  )}
                  required
                />
              </motion.div>

              {/* Attachments Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={arClass(
                  `p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <h2 className={arClass(`text-lg max-sm:text-sm font-bold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`)}>  
                  <Text>Attachments</Text> <span className="text-sm font-normal">(Optional)</span>
                </h2>
                <div
                  className={arClass(
                    `p-4 rounded-lg border-2 border-dashed transition-all cursor-pointer ${
                      darkMode
                        ? "bg-gray-700/20 border-gray-600/30 hover:border-amber-500/50"
                        : "bg-gray-50 border-gray-300 hover:border-amber-400"
                    }`
                  )}
                >
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-input"
                  />
                  <label htmlFor="file-input" className="cursor-pointer">
                    <p className={arClass(`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                      <Text>Click to upload or drag and drop</Text>
                    </p>
                    <p className={arClass(`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`)}>
                      <Text>PNG, JPG, PDF up to 10MB</Text>
                    </p>
                  </label>
                </div>

                {/* Attached Files List */}
                {formData.attachments.length > 0 && (
                  <div className={arClass("mt-4 space-y-2")}>
                    {formData.attachments.map((file, index) => (
                      <div
                        key={index}
                        className={arClass(
                          `flex items-center justify-between p-3 rounded-lg ${
                            darkMode ? "bg-gray-700/30 border border-gray-600/30" : "bg-gray-100 border border-gray-200"
                          }`
                        )}
                      >
                        <span className={arClass(`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className={arClass(`text-sm font-medium ${darkMode ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-700"}`)}
                        >
                          <Text>Remove</Text>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Submit & Cancel Buttons */}
              <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className={arClass("flex gap-4 pt-4")}
                  >
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={arClass(
                    `flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold transition-all ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : darkMode
                          ? "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white shadow-lg shadow-amber-600/50"
                          : "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/50"
                    }`
                  )}
                    >
                      <Save className="w-5 h-5" />
                      <Text>
                        {loading ? "Creating Request..." : "Create Request"}
                      </Text>
                    </motion.button>
                  </motion.div>
              
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateHelpRequest;
